import { ConfigProvider } from "antd";
import { useApplicationViewSettings } from "../../../../../containers/ApplicationPage/ApplicationViewContext";
import React from "react";
import { useTranslation } from "react-i18next";
import { Descriptions, Typography } from "antd";
import { useSelector } from "react-redux";
import dayjs from "dayjs";
import { Link } from "react-router-dom";
import { ApplicationUtils } from "../../../../../viewModels/Applications/ApplicationModel";
import { makeSelectCompanySettings, makeSelectDisplayScope, makeSelectLanguage } from "../../../../../containers/App/selectors";
import LocationWithIcon from "../../components/LocationWithIcon";
import LPInput from "../../../../LongpollControls/LPInput";
import LPTextArea from "../../../../LongpollControls/LPTextArea";
import DealLink from "../../../../DealLink";
import BlackListedCompanyName from "../../../../BlackListedCompanyName";
import BitrixCompanySelect from "../../../../BitrixCompanySelect";
import type { ApplicationGeneralInfoProps } from "../../types";

const ApplicationGeneralInfo = React.memo(
  ({ application, onChange, canEditApplicationFields, canEditApplicationComment, onCommit }: ApplicationGeneralInfoProps) => {
    const { t } = useTranslation("translations");
    const lang = useSelector(makeSelectLanguage());
    const viewSettings = useApplicationViewSettings();
    const displayScope = useSelector(makeSelectDisplayScope());
    const companySettings = useSelector(makeSelectCompanySettings());
    const isChineseOffice = (viewSettings?.officeType ?? companySettings.officeType) === "CHINESE_OFFICE";
    let clientNameCmp;
    if (application.bitrixCompanyInfo) {
      if (application.bitrixCompanyInfo.isBlackList) {
        clientNameCmp = <BlackListedCompanyName name={application.bitrixCompanyInfo.name} />;
      } else {
        clientNameCmp = application.bitrixCompanyInfo.name;
      }
    } else {
      clientNameCmp = <>{"-"}</>;
    }
    return (
      <ConfigProvider
        theme={{
          token: { fontSize: 12 },
          components: {
            Select: {
              activeBorderColor: "transparent",
              activeOutlineColor: "transparent",
            },
            Input: {
              activeBorderColor: "transparent",
              activeShadow: "none",
              inputFontSize: 12,
              inputFontSizeSM: 12,
              inputFontSizeLG: 12,
              paddingBlock: 0,
              paddingInline: 0,
              paddingBlockSM: 0,
              paddingInlineSM: 0,
              paddingBlockLG: 0,
              paddingInlineLG: 0,
            },
            InputNumber: {
              activeBorderColor: "transparent",
              activeShadow: "none",
              inputFontSize: 12,
              inputFontSizeSM: 12,
              inputFontSizeLG: 12,
              paddingBlock: 0,
              paddingInline: 0,
              paddingBlockSM: 0,
              paddingInlineSM: 0,
              paddingBlockLG: 0,
              paddingInlineLG: 0,
            },
          },
        }}
        select={{ styles: { root: { padding: 0 } } }}
        textArea={{ styles: { textarea: { minHeight: 22 } } }}
        descriptions={{
          styles: {
            label: { padding: "6px 8px" },
            content: { padding: "6px 8px" },
          },
        }}
      >
        <Descriptions
          bordered
          column={3}
          size="small"
          items={[
            { label: t("application_number"), children: application.number },
            {
              label: t("transportation_type"),
              children: ApplicationUtils.getTransportCharacteristicsTypeLabelByType(application.type, lang),
            },
            { label: t("incoterms_type"), children: application.incotermsType },
            { label: t("application_name"), children: application.name },
            { label: t("manager"), children: application.manager.name },
            {
              label: t("logistician"),
              children: application.logistician?.name || "-",
            },
            {
              label: t("client_name"),
              span: 3,
              children: canEditApplicationFields ? (
                <BitrixCompanySelect
                  value={application.bitrixCompanyInfo}
                  onChange={(value) => {
                    if (value) {
                      onChange({
                        ...application,
                        clientInfo: { id: application.clientInfo?.id!, contractorId: value.id, name: value.name },
                        bitrixCompanyInfo: value,
                      });
                      onCommit?.();
                    }
                  }}
                  allowClear={false}
                  variant="borderless"
                  suffixIcon={null}
                  companyType="CLIENT"
                />
              ) : (
                clientNameCmp
              ),
            },
            isChineseOffice && {
              label: t("client_manager"),
              span: 3,
              children: canEditApplicationFields ? (
                <LPInput
                  value={application.clientManager || ""}
                  variant="borderless"
                  onChange={(e) => {
                    onChange({ ...application, clientManager: e.target.value || undefined });
                    onCommit?.();
                  }}
                />
              ) : (
                application.clientManager || "-"
              ),
            },
            {
              label: t("from"),
              children: application.filter.fromLocation && <LocationWithIcon location={application.filter.fromLocation} />,
            },
            {
              label: t("pickup_address"),
              children: canEditApplicationFields ? (
                <LPTextArea
                  value={application.cargoInfo?.pickupAddress}
                  variant="borderless"
                  autoSize
                  onChange={(e) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, pickupAddress: e.target.value } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.pickupAddress || "-"
              ),
            },
            {
              label: t("date_of_ready_cargo"),
              children: application.filter.fromDate ? dayjs(application.filter.fromDate).locale(lang).format("DD MMM YYYY") : "-",
            },
            {
              label: t("to"),
              children: application.filter.toLocation && <LocationWithIcon location={application.filter.toLocation} />,
            },
            {
              label: t("delivery_address"),
              children: canEditApplicationFields ? (
                <LPTextArea
                  value={application.cargoInfo?.deliveryAddress}
                  variant="borderless"
                  autoSize
                  onChange={(e) => {
                    onChange({ ...application, cargoInfo: { ...application.cargoInfo, deliveryAddress: e.target.value } });
                    onCommit?.();
                  }}
                />
              ) : (
                application.cargoInfo?.deliveryAddress || "-"
              ),
            },
            {
              label: t("date_of_delivery"),
              children: application.filter.toDate ? dayjs(application.filter.toDate).locale(lang).format("DD MMM YYYY") : "-",
            },
            application.commercialOffer && {
              span: 3,
              label: t("commercial_offer_short"),
              children:
                !viewSettings && displayScope === "ALL" ? (
                  <Link to={`/commercial-offers/${application.commercialOffer.id}`}>
                    {application.commercialOffer.number} {application.commercialOffer.name}
                  </Link>
                ) : (
                  `${application.commercialOffer.number} ${application.commercialOffer.name}`
                ),
            },
            application.deal && {
              span: 3,
              label: t("deal"),
              children: viewSettings ? (
                <Link {...viewSettings.getDealLink(application.deal.id)}>{application.deal.number}</Link>
              ) : (
                <DealLink id={application.deal.id} number={application.deal.number} />
              ),
            },
            {
              span: 3,
              label: t("comment"),
              children: canEditApplicationComment ? (
                <LPTextArea
                  value={application.comment}
                  variant="borderless"
                  autoSize
                  onChange={(e) => {
                    onChange({ ...application, comment: e.target.value });
                    onCommit?.();
                  }}
                />
              ) : (
                <Typography.Text style={{ whiteSpace: "pre-wrap" }}>{application.comment || "-"}</Typography.Text>
              ),
            },
          ]
            .flat(3)
            .filter((item) => !!item)}
        />
      </ConfigProvider>
    );
  },
);
export default ApplicationGeneralInfo;
